import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import { BarChart3, ClipboardCheck, History, Landmark, Scale, Settings, Trash2, Users } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { buttonClass } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageHeader } from '@/components/ui/PageHeader'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { describeRoles } from '@/auth/profile'
import { useProfile } from '@/auth/SessionProvider'
import { friendlyAuthError } from '@/data/auth'
import { listDepartments } from '@/data/departments'
import type { Department } from '@/data/departments'
import { listWaitingBudgets } from '@/data/budgets'
import { listWaitingExpenses } from '@/data/expenses'
import { quarterIncome } from '@/data/income'
import { listOpenExceptions } from '@/data/reconciliation'
import { formatKwacha } from '@/lib/money'
import { currentQuarterId } from '@/lib/periods'
import { ChurchCharts, KpiRow, RecentActivity } from './ChurchInsights'
import { greetingText } from './Greeting'
import { NavCardFace, navCardClass } from './NavCardFace'
import { quarterLabel } from './insights'

type AdminRoute =
  | '/admin/approvals'
  | '/admin/reconcile'
  | '/admin/income'
  | '/admin/reports'
  | '/admin/people'
  | '/admin/settings'
  | '/admin/deletions'
  | '/admin/history'

/** A way into one screen: its icon, name, what it is for, and a live figure where there is one. */
function NavCard({ to, ...face }: { to: AdminRoute; icon: LucideIcon; title: string; description: string; live?: string; attention?: boolean }) {
  return (
    <Link to={to} className={navCardClass}>
      <NavCardFace {...face} />
    </Link>
  )
}

/** Landing page for administrators and auditors: the whole church at a glance. */
export function AdminHome() {
  const profile = useProfile()
  const depts = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
  const nameOf = (id: string) => depts.data?.find((d) => d.id === id)?.name
  const quarter = currentQuarterId()

  const waitingBudgets = useQuery({ queryKey: ['waiting-budgets'], queryFn: listWaitingBudgets })
  const waitingExpenses = useQuery({ queryKey: ['waiting-expenses'], queryFn: listWaitingExpenses })
  const exceptions = useQuery({ queryKey: ['exceptions'], queryFn: () => listOpenExceptions() })
  const income = useQuery({ queryKey: ['income', 'quarter', quarter], queryFn: () => quarterIncome(quarter) })

  const waiting = waitingBudgets.data && waitingExpenses.data ? waitingBudgets.data.length + waitingExpenses.data.length : null
  const queries = exceptions.data?.length ?? null
  const counted = income.data ? income.data.reduce((s, r) => s + r.totalNgwee, 0) : null

  const greeting = greetingText(profile.fullName)
  // "Administrator · Wednesday 7 October" (the quarter is on the figures below, where it matters).
  const today = greeting.today.split(' · ')[0]
  const meta = `${describeRoles(profile, nameOf).join(' · ')} · ${today}${profile.isAdmin ? '' : ' — you can look at everything but not change it.'}`

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={greeting.hello}
        description={meta}
        actions={
          profile.isAdmin && (
            <>
              <Link to="/admin/income" className={buttonClass('primary')}>
                Count a Sabbath
              </Link>
              <Link to="/admin/reconcile" className={buttonClass('secondary')}>
                Record money received
              </Link>
            </>
          )
        }
      />

      <KpiRow />

      <section aria-labelledby="daily-work" className="flex flex-col gap-3">
        <h3 id="daily-work" className="text-section font-semibold text-ink">
          Daily work
        </h3>
        <div className="grid gap-3 lg:grid-cols-2">
          <NavCard
            to="/admin/approvals"
            icon={ClipboardCheck}
            title="Approvals"
            description="Open an item to approve it or send it back"
            live={waiting === null ? undefined : waiting === 0 ? 'None waiting' : `${waiting} waiting`}
            attention={!!waiting}
          />
          <NavCard
            to="/admin/reconcile"
            icon={Scale}
            title="Reconcile"
            description="Match money received to what departments sent"
            live={queries === null ? undefined : queries === 0 ? 'All accounted for' : `${queries} open ${queries === 1 ? 'query' : 'queries'}`}
            attention={!!queries}
          />
          <NavCard
            to="/admin/income"
            icon={Landmark}
            title="Church income"
            description="Tithes and offerings counted each Sabbath, and what is passed on"
            live={counted === null ? undefined : `${formatKwacha(counted)} in ${quarterLabel(quarter)}`}
          />
          <NavCard
            to="/admin/reports"
            icon={BarChart3}
            title="Church summary"
            description="Every department side by side for a quarter"
          />
        </div>
      </section>

      <ChurchCharts departments={depts.data?.filter((d) => !d.is_central)} />

      <div className="grid gap-4 lg:grid-cols-3">
        <RecentActivity className="lg:col-span-2" />
        <DepartmentsList depts={depts} />
      </div>

      <section aria-labelledby="manage" className="flex flex-col gap-3">
        <h3 id="manage" className="text-section font-semibold text-ink">
          Manage
        </h3>
        <div className="grid gap-3 lg:grid-cols-2">
          <NavCard to="/admin/people" icon={Users} title="People" description="Accounts, roles and passwords" />
          <NavCard to="/admin/settings" icon={Settings} title="Settings" description="Approval rule, departments, categories" />
          <NavCard to="/admin/history" icon={History} title="History" description="Who added, changed or deleted what, and when" />
          <NavCard to="/admin/deletions" icon={Trash2} title="Deletion log" description="What the owner has permanently deleted, and why" />
        </div>
      </section>
    </div>
  )
}

function DepartmentsList({ depts }: { depts: ReturnType<typeof useQuery<Department[]>> }) {
  return (
    <Card>
      <CardHeader title="Departments" />
      <CardBody>
        {depts.isPending ? (
          <SkeletonRows rows={4} />
        ) : depts.isError ? (
          <p role="alert" className="text-body text-bad">
            {friendlyAuthError(depts.error)}
          </p>
        ) : depts.data.length === 0 ? (
          <EmptyState title="No departments yet" description="Departments will appear here once they are created." />
        ) : (
          <ul className="divide-y divide-line">
            {depts.data.map((d) => (
              <li key={d.id}>
                <Link
                  to="/d/$deptId"
                  params={{ deptId: d.id }}
                  className="-mx-2 flex min-h-[44px] items-center justify-between gap-3 rounded-control px-2 py-3 hover:bg-page"
                >
                  <span className="font-medium text-ink">{d.name}</span>
                  {d.is_central && <Badge tone="info">Central treasury</Badge>}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}
